import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Monitor, 
  Mail, 
  MessageSquare, 
  Clock, 
  Globe,
  Send,
  CheckCircle,
  AlertTriangle,
  Loader
} from 'lucide-react';
import { getCanonicalUrl } from '../utils/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Form submitted successfully:', response.data);
      setSubmitted(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - URL Monitor Pro | Get Support & Sales Help</title>
        <meta name="description" content="Get in touch with URL Monitor Pro team for support, sales inquiries, or partnership opportunities. We're here to help with your website monitoring needs." />
        <meta name="keywords" content="contact URL Monitor Pro, support, sales, help, customer service, website monitoring support" />
        <link rel="canonical" href={getCanonicalUrl('/contact')} />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Monitor className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <a href="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
                <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/contact" className="text-blue-600 font-semibold">Contact</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have questions about URL Monitor Pro? We're here to help with sales inquiries, technical support, and partnership opportunities.
            </p>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
                
                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div className="flex items-center">
                      <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-green-800">Message sent!</h3>
                        <p className="text-green-700">We'll get back to you within 24 hours.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-4 text-green-600 hover:text-green-700 font-medium text-sm underline"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        {error}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                          placeholder="Your full name"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      >
                        <option value="">Select a subject</option>
                        <option value="sales">Sales Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="billing">Billing Question</option>
                        <option value="partnership">Partnership</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                        placeholder="Tell us how we can help..."
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Other ways to reach us</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex items-start">
                      <div className="bg-blue-100 rounded-lg p-3 mr-4">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
                        <p className="text-gray-600 mb-2">For general inquiries and support</p>
                        <a href="mailto:support@urlmonitor.pro" className="text-blue-600 hover:text-blue-700 font-medium">
                          support@urlmonitor.pro
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex items-start">
                      <div className="bg-green-100 rounded-lg p-3 mr-4">
                        <MessageSquare className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Live Chat</h3>
                        <p className="text-gray-600 mb-2">Chat with our support team</p>
                        <p className="text-green-600 font-medium">Available 9 AM - 5 PM PST</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-6">
                    <div className="flex items-start">
                      <div className="bg-purple-100 rounded-lg p-3 mr-4">
                        <Clock className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Time</h3>
                        <p className="text-gray-600 mb-2">We aim to respond to all inquiries within:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Sales: Within 2 hours</li>
                          <li>• Support: Within 4 hours</li>
                          <li>• General: Within 24 hours</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6">
                    <div className="flex items-start">
                      <div className="bg-orange-100 rounded-lg p-3 mr-4">
                        <Globe className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Support</h3>
                        <p className="text-gray-600 mb-2">We support customers worldwide</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• English support 24/7</li>
                          <li>• Indian timezone coverage</li>
                          <li>• Multiple payment methods</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900">Can I start with a free trial?</h4>
                      <p className="text-sm text-gray-600">Yes! Our free plan allows you to monitor 1 website forever.</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Do you offer custom pricing?</h4>
                      <p className="text-sm text-gray-600">Yes, we offer custom plans for enterprises. Contact sales for details.</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">What payment methods do you accept?</h4>
                      <p className="text-sm text-gray-600">Credit cards globally, UPI/cards/net banking in India.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-600 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to start monitoring?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Don't wait for your website to go down. Start monitoring today with our free plan.
            </p>
            <Link 
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Free Monitoring
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Link to="/" className="flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-xl font-bold">URL Monitor Pro</span>
              </Link>
              <p className="text-gray-400">
                © 2024 URL Monitor Pro. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContactPage;